import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, ExternalLink } from "lucide-react";
import { getTopVideos30d, refreshTopVideos30d } from "@/lib/api";

function nFmt(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function fmtDuration(s) {
  if (!s || s <= 0) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function fmtPublished(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.floor((now - then) / 86400_000);
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${days}d ago`;
}

export default function TopVideos30dPanel({ embedded = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [facelessOnly, setFacelessOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getTopVideos30d();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load top videos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while a background scan is running so the grid populates
  // automatically. 5s cadence — scan takes ~30-90s depending on how
  // many channels need first-time classification.
  useEffect(() => {
    if (!data) return undefined;
    const running = data.status === "running" || data.in_flight_status === "running";
    if (!running) return undefined;
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
  }, [data, load]);

  const onRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const d = await refreshTopVideos30d(true);
      setData(d);
      const n = (d?.videos || []).length;
      if (d?.quota_exhausted) {
        toast.warning("YouTube quota exhausted — showing last cached results.");
      } else if (d?.status === "running" || d?.in_flight_status === "running") {
        toast.info("Scanning YouTube… this takes ~30-90 seconds. Results update automatically.");
      } else {
        toast.success(`Refreshed ${n} video${n === 1 ? "" : "s"}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const allVideos = data?.videos || [];
  const videos = facelessOnly
    ? allVideos.filter((v) => v.is_faceless)
    : allVideos;
  const facelessCount = allVideos.filter((v) => v.is_faceless).length;
  const ageH = data?.age_hours;
  const stale = data?.stale;
  const empty = !loading && videos.length === 0;

  return (
    <section
      data-testid="top-videos-30d-panel"
      className={embedded ? "mt-16 pt-10 border-t border-zinc-900" : ""}
    >
      {embedded && (
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
          {"// TRENDING · WHAT'S WINNING RIGHT NOW"}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-white leading-none mb-2">
            Most-viewed YouTube videos, last 30 days.
          </h2>
          <p className="text-zinc-400 text-sm max-w-2xl leading-relaxed">
            Global English-language search across all of YouTube — top 50
            by real view count. Great for spotting formats catching fire
            outside your usual niches.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setFacelessOnly((v) => !v)}
            data-testid="top-videos-30d-faceless-filter"
            aria-pressed={facelessOnly}
            title={
              facelessOnly
                ? "Showing only faceless / no-on-camera-host videos. Click to show all."
                : "Show only videos from faceless / no-on-camera-host channels (stock B-roll, animation, gameplay, tutorials, etc.)"
            }
            className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2.5 rounded-sm border transition-colors ${
              facelessOnly
                ? "bg-yellow-400 text-black border-yellow-400"
                : "text-zinc-300 hover:text-yellow-400 border-zinc-800 hover:border-yellow-400"
            }`}
          >
            faceless only
            <span className={`font-mono text-[10px] ${facelessOnly ? "text-black/70" : "text-zinc-500"}`}>
              · {facelessCount}
            </span>
          </button>
          {ageH != null && (
            <span
              data-testid="top-videos-30d-cache-age"
              className={`font-mono text-[11px] uppercase tracking-[0.25em] ${
                stale ? "text-amber-400" : "text-zinc-500"
              }`}
            >
              cached {ageH < 1 ? `${Math.round(ageH * 60)}m` : `${ageH}h`} ago{stale ? " · stale" : ""}
            </span>
          )}
          <button
            data-testid="top-videos-30d-refresh-btn"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
          >
            {refreshing
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <RefreshCcw className="w-3.5 h-3.5" />}
            {refreshing ? "refreshing…" : "refresh"}
          </button>
        </div>
      </div>

      {(data?.status === "failed" || data?.in_flight_status === "failed") && (
        <div
          data-testid="top-videos-30d-failure-banner"
          className="border border-red-500/50 bg-red-500/[0.06] rounded-sm px-4 py-3 mb-6"
        >
          <div className="font-mono text-[10px] font-black uppercase tracking-[0.3em] text-red-400 mb-1">
            {"// SCAN FAILED"}
          </div>
          <div className="text-[12px] text-red-200">
            {data?.error === "scan_timed_out_or_pod_restarted"
              ? "The scan didn't finish — the server may have restarted mid-scan. Hit refresh to try again."
              : (data?.error || "Something went wrong. Hit refresh to retry.")}
          </div>
        </div>
      )}

      {(data?.status === "running" || data?.in_flight_status === "running") && (
        <div
          data-testid="top-videos-30d-running-banner"
          className="border border-yellow-400/60 bg-yellow-400/[0.06] rounded-sm px-4 py-3 mb-6 flex items-center gap-3"
        >
          <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
          <div>
            <div className="font-mono text-[10px] font-black uppercase tracking-[0.3em] text-yellow-400 mb-0.5">
              {"// SCANNING YOUTUBE"}
            </div>
            <div className="text-[12px] text-zinc-300">
              Pulling videos and classifying every channel. Takes about a minute — this page updates automatically.
            </div>
          </div>
        </div>
      )}

      {data?.quota_exhausted && (
        <div
          data-testid="top-videos-30d-quota-warning"
          className="border border-amber-500/40 bg-amber-500/[0.06] rounded-sm px-4 py-3 mb-6"
        >
          <div className="font-mono text-[10px] font-black uppercase tracking-[0.3em] text-amber-400 mb-1">
            // YOUTUBE QUOTA EXHAUSTED
          </div>
          <div className="text-[12px] text-amber-200">
            Showing the last cached results. Quota resets at midnight
            Pacific — or add another YOUTUBE_API_KEY_N to your .env.
          </div>
        </div>
      )}

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">[recon] loading top videos<span className="blink">_</span></div>
      ) : empty ? (
        <div className="border border-zinc-800 rounded-sm p-8 text-center text-zinc-400 text-sm">
          {facelessOnly ? (
            <>
              No faceless videos in the current top 50. Try clearing the filter
              or hitting <b className="text-yellow-400">refresh</b> for the
              latest scan.
            </>
          ) : (
            <>
              No videos yet. Hit <b className="text-yellow-400">refresh</b> to
              pull the latest.
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videos.map((v) => (
            <VideoCard
              key={v.video_id}
              v={v}
              rank={allVideos.indexOf(v) + 1}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function VideoCard({ v, rank }) {
  return (
    <a
      href={v.video_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`top-video-card-${v.video_id}`}
      className="group block border border-zinc-900 hover:border-yellow-400/60 rounded-sm overflow-hidden bg-zinc-950/60 transition-colors"
    >
      <div className="relative aspect-video bg-zinc-900 overflow-hidden">
        {v.thumbnail_url ? (
          <img
            src={v.thumbnail_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : null}
        <div className="absolute top-2 left-2 font-mono text-[10px] font-black uppercase tracking-[0.2em] bg-yellow-400 text-black px-1.5 py-0.5 rounded-sm">
          #{String(rank).padStart(2, "0")}
        </div>
        {v.duration_seconds > 0 && (
          <div className="absolute bottom-2 right-2 font-mono text-[10px] bg-black/85 text-white px-1.5 py-0.5 rounded-sm">
            {v.is_short ? "SHORT · " : ""}{fmtDuration(v.duration_seconds)}
          </div>
        )}
      </div>
      <div className="p-3.5 space-y-2">
        <h3
          className="text-[13px] font-bold text-white leading-snug line-clamp-2 group-hover:text-yellow-300 transition-colors"
          title={v.title}
        >
          {v.title}
        </h3>
        <div className="text-[11px] text-zinc-400 truncate" title={v.channel_title}>
          {v.channel_title}
        </div>
        {(v.niche || v.is_faceless) && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            {v.niche && (
              <span
                data-testid={`top-video-niche-${v.video_id}`}
                className="inline-flex items-center font-mono text-[9px] font-bold uppercase tracking-[0.18em] bg-zinc-900 border border-zinc-800 text-yellow-300 px-1.5 py-0.5 rounded-sm"
              >
                {v.niche}
              </span>
            )}
            {v.is_faceless && (
              <span
                data-testid={`top-video-faceless-${v.video_id}`}
                title="Faceless format — no consistent on-camera human host"
                className="inline-flex items-center font-mono text-[9px] font-bold uppercase tracking-[0.18em] bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 px-1.5 py-0.5 rounded-sm"
              >
                faceless
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 pt-1 font-mono text-[11px] text-zinc-300">
          <span className="text-yellow-400 font-bold">
            {nFmt(v.views)} views
          </span>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500">{fmtPublished(v.published_at)}</span>
          <span className="ml-auto text-zinc-600 group-hover:text-yellow-400 transition-colors">
            <ExternalLink className="w-3 h-3" />
          </span>
        </div>
      </div>
    </a>
  );
}
